/**
 * Toast 通知工具
 * 基于 react-toastify 的统一封装，保证全局样式一致
 */

import { toast, ToastOptions, Id } from 'react-toastify';

/** 默认配置 */
const DEFAULT_OPTIONS: ToastOptions = {
  position: 'top-center',
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: false,
};

/** 成功提示默认配置 */
const SUCCESS_OPTIONS: ToastOptions = {
  ...DEFAULT_OPTIONS,
  autoClose: 2000,
};

/** 错误提示默认配置 */
const ERROR_OPTIONS: ToastOptions = {
  ...DEFAULT_OPTIONS,
};

/**
 * 显示成功提示
 * @param message 提示信息
 * @param options 可选配置（会覆盖默认配置）
 * @returns toast ID
 */
const success = (message: string, options?: ToastOptions): Id => {
  return toast.success(message, { ...SUCCESS_OPTIONS, ...options });
};

/**
 * 显示信息提示
 * @param message 提示信息
 * @param options 可选配置（会覆盖默认配置）
 * @returns toast ID
 */
const info = (message: string, options?: ToastOptions): Id => {
  return toast.info(message, { ...DEFAULT_OPTIONS, ...options });
};

/**
 * 显示警告提示
 * @param message 提示信息
 * @param options 可选配置（会覆盖默认配置）
 * @returns toast ID
 */
const warning = (message: string, options?: ToastOptions): Id => {
  return toast.warning(message, { ...DEFAULT_OPTIONS, ...options });
};

/**
 * 显示错误提示
 * @param message 提示信息
 * @param options 可选配置（会覆盖默认配置）
 * @returns toast ID
 */
const error = (message: string, options?: ToastOptions): Id => {
  return toast.error(message, { ...ERROR_OPTIONS, ...options });
};

/**
 * 关闭指定 toast
 * @param toastId toast ID（不传则关闭所有）
 */
const dismiss = (toastId?: Id): void => {
  if (toastId) {
    toast.dismiss(toastId);
  } else {
    toast.dismiss();
  }
};

/**
 * Toast 通知工具
 * @example
 * import { showToast } from '../helpers/toast';
 *
 * showToast.success('保存成功');
 * showToast.error('操作失败');
 * showToast.info('请在右侧选择标签');
 * showToast.warning('请先填写必填项');
 * showToast.info('自定义样式', { style: { backgroundColor: '#333' } });
 *
 * // 自定义配置
 * showToast.success('操作成功', { autoClose: 1000 });
 */
export const showToast = {
  success,
  info,
  warning,
  error,
  dismiss,
};

export default showToast;
